import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import React, { useMemo } from 'react';
import { useState, useCallback } from 'react';

import { MultiImageValue } from './MultiImageUploadInput';

type Props = {
  onUpdateImageOrder: (updatedList: MultiImageValue[]) => void;
  originalList: MultiImageValue[];
  children: (data: { displayImageList: MultiImageValue[] }) => React.ReactNode;
};

const MultiImageDndContext = ({ children, onUpdateImageOrder, originalList }: Props) => {
  const [localImageList, setLocalImageList] = useState<MultiImageValue[] | null>(null);

  const handleDragStart = useCallback(() => {
    setLocalImageList(originalList);
  }, [originalList]);

  const handleDragEnd = useCallback(
    async ({ over, active }: DragEndEvent) => {
      try {
        if (!over || active?.id === over.id) {
          return;
        }
        const oldIndex = originalList.findIndex(image => image.id === active.id);
        const newIndex = originalList.findIndex(image => image.id === over.id);
        const updatedImageList = arrayMove(originalList, oldIndex, newIndex);
        onUpdateImageOrder(updatedImageList);
      } finally {
        setLocalImageList(null);
      }
    },
    [onUpdateImageOrder, originalList],
  );

  const displayImageList = useMemo(() => localImageList || originalList, [localImageList, originalList]);

  return (
    <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd} collisionDetection={closestCenter}>
      <SortableContext strategy={verticalListSortingStrategy} items={displayImageList.map(image => image.id)}>
        {children({ displayImageList })}
      </SortableContext>
    </DndContext>
  );
};

export default MultiImageDndContext;
